import type { IconProps } from '@/components/ui/icon';
import { safeLocalStorage } from '@/lib/safeStorage';
import sv from '../../../i18n/sv-SE.json';
import {
  type UserRole,
  combineRoleRequirement,
  hasRequiredRole,
  normalizeUserRole,
} from '@/types/user';

type NavigationNodeBase = {
  label: string;
  icon?: IconProps['name'];
  minRole?: UserRole;
};

export type NavigationNode = NavigationNodeBase &
  (
    | {
        path: string;
        items?: NavigationNode[];
      }
    | {
        path?: undefined;
        items: NavigationNode[];
      }
  );

export type FlatNavigationItem = {
  label: string;
  path: string;
  icon?: IconProps['name'];
  parents: string[];
  fullLabel: string;
  minRole?: UserRole;
};

const getLeaveOverviewMinimumRole = (): UserRole => {
  const stored = safeLocalStorage.getItem('leaveOverviewMinimumRole');
  return normalizeUserRole(stored ?? 'User');
};

const applyLeaveOverviewMinimumRole = (
  path: string | undefined,
  minRole: UserRole | undefined,
): UserRole | undefined => {
  if (!path) {
    return minRole;
  }

  if (path === '/leave/calendar') {
    return combineRoleRequirement(minRole, getLeaveOverviewMinimumRole());
  }

  return minRole;
};

export const navigationTree: NavigationNode[] = [
  { label: 'Översikt', path: '/', icon: 'Home' },
  {
    label: 'Projekt',
    icon: 'FolderKanban',
    items: [
      { label: 'Projekt', path: '/projects' },
      { label: 'Dokument', path: '/documents' },
      {
        label: 'Egenkontrollmallar',
        path: '/projects/inspection-templates',
        minRole: 'Administrator',
      },
      { label: 'Personalliggare', path: '/stafflogs' },
    ],
  },
  {
    label: 'Miljö & säkerhet',
    icon: 'FlaskConical',
    items: [
      { label: 'Tillstånd', path: '/permits', minRole: 'ProjectManager' },
      { label: 'Kemikalier', path: '/chemicals' },
    ],
  },
  {
    label: 'Tid',
    icon: 'Clock',
    items: [
      { label: 'Tidrapportering', path: '/time' },
      { label: 'Tidsöversikt', path: '/time/overview' },
      {
        label: 'Tidgranskning',
        path: '/time/approval',
        minRole: 'ProjectManager',
      },
    ],
  },
  {
    label: sv.project.absence.title,
    icon: 'Umbrella',
    items: [
      { label: sv.project.absence.mine, path: '/leave' },
      {
        label: 'Attest',
        path: '/leave/approvals',
        minRole: 'ProjectManager',
      },
      {
        label: 'Översikt',
        path: '/leave/calendar',
        minRole: 'User',
      },
      {
        label: 'Perioder',
        path: '/leave/seasons',
        minRole: 'Administrator',
      },
      {
        label: 'Saldon',
        path: '/leave/balances',
        minRole: 'Administrator',
      },
      {
        label: 'Delegeringar',
        path: '/leave/delegations',
        minRole: 'Administrator',
      },
      {
        label: 'Spärrperioder',
        path: '/leave/blackouts',
        minRole: 'Administrator',
      },
      {
        label: 'Lönearts-koppling',
        path: '/leave/wage-types',
        minRole: 'Administrator',
      },
    ],
  },
  {
    label: 'Ekonomi',
    icon: 'BarChart2',
    minRole: 'ProjectManager',
    items: [
      { label: 'Ekonomiöversikt', path: '/finance' },
      { label: 'Upparbetat', path: '/finance/ongoing-work' },
      { label: 'Budgetar', path: '/budgets' },
      { label: 'Byggkalkyl', path: '/calculations' },
      { label: 'Lönearter', path: '/wagetypes' },
      { label: 'Kontoplan', path: '/chartofaccounts' },
      { label: 'Momstyper', path: '/vattypes' },
    ],
  },
  {
    label: 'Inköp',
    icon: 'ShoppingCart',
    minRole: 'ProjectManager',
    items: [
      { label: 'Inköpsöversikt', path: '/purchases' },
      { label: 'Artiklar', path: '/items' },
    ],
  },
  {
    label: 'Resurser',
    icon: 'Package',
    items: [
      { label: 'Resurser', path: '/resources' },
      { label: 'Resurstyper', path: '/resourcetypes' },
      { label: 'Resursgrupper', path: '/resourcegroups' },
      { label: 'Enheter', path: '/units' },
      { label: 'Anställda', path: '/employees' },
    ],
  },
  {
    label: 'Aktörer',
    icon: 'Users',
    items: [
      { label: 'Kunder', path: '/customers' },
      { label: 'Leverantörer', path: '/vendors' },
      { label: 'Kontakter', path: '/contacts' },
    ],
  },
  {
    label: 'Planering',
    icon: 'Calendar',
    items: [
      { label: 'Översikt', path: '/planning/overview' },
      { label: 'Tidplaner', path: '/planning/schedules' },
      { label: 'Anställda', path: '/planner' },
      { label: 'Resurser', path: '/resourceplanner' },
      { label: 'Jour & skift', path: '/planning/shifts' },
      { label: 'Skiftmallar', path: '/planning/shifts/templates' },
    ],
  },
  {
    label: 'Administration',
    icon: 'Settings',
    minRole: 'Administrator',
    items: [
      { label: 'Användare', path: '/users' },
      { label: 'Sessionaudit', path: '/session-audit' },
      { label: 'Inställningar', path: '/settings' },
      { label: 'Kategorier', path: '/categories' },
      { label: 'Klassificeringskoder', path: '/classification-codes' },
      { label: 'Projektmappar', path: '/projectfolders' },
      {
        label: 'E-post',
        icon: 'Mail',
        items: [
          { label: 'Inställningar', path: '/emailsettings' },
          { label: 'Mallar', path: '/emailtemplates' },
          { label: 'Logg', path: '/emaillogs' },
        ],
      },
    ],
  },
  { label: 'Hjälp', path: '/help', icon: 'BookOpen' },
  { label: 'Changelog', path: '/changelog', icon: 'History' },
  {
    label: 'Förbättringsförslag',
    path: '/feedback',
    icon: 'BookOpen',
  },
];

const buildFlatNavigation = (
  nodes: NavigationNode[],
  parents: string[] = [],
  inheritedMinRole?: UserRole,
): FlatNavigationItem[] => {
  return nodes.flatMap((node) => {
    const currentParents = [...parents, node.label];
    let effectiveMinRole = combineRoleRequirement(
      inheritedMinRole,
      node.minRole,
    );
    effectiveMinRole = applyLeaveOverviewMinimumRole(
      'path' in node ? node.path : undefined,
      effectiveMinRole,
    );

    if ('items' in node && node.items?.length) {
      const childItems = buildFlatNavigation(
        node.items,
        currentParents,
        effectiveMinRole,
      );

      return 'path' in node
        ? [
            {
              label: node.label,
              path: node.path as string,
              icon: node.icon,
              parents,
              fullLabel: currentParents.join(' / '),
              minRole: effectiveMinRole,
            },
            ...childItems,
          ]
        : childItems;
    }

    if ('path' in node) {
      return [
        {
          label: node.label,
          path: node.path as string,
          icon: node.icon,
          parents,
          fullLabel: currentParents.join(' / '),
          minRole: effectiveMinRole,
        },
      ];
    }

    return [];
  });
};

export const navigationItems: FlatNavigationItem[] = buildFlatNavigation(
  navigationTree,
);

const filterNavigationNodesByRole = (
  nodes: NavigationNode[],
  role: UserRole,
  inheritedMinRole?: UserRole,
): NavigationNode[] => {
  return nodes
    .map((node) => {
      let effectiveMinRole = combineRoleRequirement(
        inheritedMinRole,
        node.minRole,
      );
      effectiveMinRole = applyLeaveOverviewMinimumRole(
        'path' in node ? node.path : undefined,
        effectiveMinRole,
      );
      const meetsRequirement = hasRequiredRole(role, effectiveMinRole);

      if ('items' in node && node.items?.length) {
        const filteredChildren = filterNavigationNodesByRole(
          node.items,
          role,
          effectiveMinRole,
        );

        if (filteredChildren.length === 0 && !meetsRequirement) {
          return null;
        }

        if ('path' in node && node.path && !meetsRequirement) {
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...node,
            items: filteredChildren,
            path: undefined,
          } as NavigationNode;
        }

        return {
          ...node,
          items: filteredChildren,
        } as NavigationNode;
      }

      if ('path' in node && node.path) {
        return meetsRequirement ? node : null;
      }

      return null;
    })
    .filter((node): node is NavigationNode => Boolean(node));
};

export const getNavigationTreeForRole = (role: UserRole) =>
  filterNavigationNodesByRole(navigationTree, role);

export const getNavigationItemsForRole = (role: UserRole) =>
  navigationItems.filter((item) => hasRequiredRole(role, item.minRole));

export const navigationItemMap = new Map(
  navigationItems.map((item) => [item.path, item] as const),
);

export const navigationPaths = new Set(navigationItemMap.keys());

export const findNavigationItemByPath = (path: string) =>
  navigationItemMap.get(path);

